import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Icon, Label, Modal, Table } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { API, showError } from '../helpers';
import globalStyles from '../styles/globalStyles';

export const PROXY_NODE_STATUSES = [
  'success',
  'failed',
  'unsupported',
  'unknown',
];
export const PROXY_NODE_MODES = [
  'egress_https',
  'tcp_endpoint',
  'unsupported',
  'unknown',
];

export const filterProxyNodes = (nodes, query) => {
  const needle = (query || '').trim().toLowerCase();
  if (!needle) return nodes || [];
  return (nodes || []).filter((node) =>
    `${node.name} ${node.protocol} ${node.host} ${node.port}`
      .toLowerCase()
      .includes(needle)
  );
};

export const normalizeProxyNodeTestResult = (value) => {
  const result = value?.result || value?.data || value || {};
  return {
    status: PROXY_NODE_STATUSES.includes(result.status)
      ? result.status
      : 'unknown',
    mode: PROXY_NODE_MODES.includes(result.mode) ? result.mode : 'unknown',
    latency: result.latency_ms ?? null,
    message: result.message || '',
    lastTestTime: result.last_test_time ?? null,
  };
};

export const getPersistedProxyNodeTestResult = (node) => {
  if (
    !node ||
    (!node.test_mode &&
      !node.test_status &&
      node.test_latency_ms == null &&
      !node.test_message &&
      !node.last_test_time)
  )
    return null;
  return normalizeProxyNodeTestResult({
    mode: node.test_mode,
    status: node.test_status,
    latency_ms: node.test_latency_ms,
    message: node.test_message,
    last_test_time: node.last_test_time,
  });
};

export const mergePersistedProxyNodeTestResults = (nodes, current = {}) => {
  const persisted = {};
  (nodes || []).forEach((node) => {
    const result = getPersistedProxyNodeTestResult(node);
    if (result) persisted[node.id] = result;
  });
  return { ...persisted, ...current };
};

export const formatProxyNodeMetric = (value) =>
  Number.isFinite(Number(value)) ? Number(value).toLocaleString() : '0';

export const formatProxyNodeResponseTime = (value) =>
  value === null || value === undefined || value === ''
    ? '-'
    : `${Number(value).toLocaleString()} ms`;

const ProxyNodesModal = ({ open, subscription, nodes, loading, onClose }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [testingIds, setTestingIds] = useState([]);
  const [testingAll, setTestingAll] = useState(false);
  const [results, setResults] = useState({});

  useEffect(() => {
    if (!open) {
      setQuery('');
      setTestingIds([]);
      setTestingAll(false);
      setResults({});
      return;
    }
    setResults((current) => mergePersistedProxyNodeTestResults(nodes, current));
  }, [nodes, open]);

  const filtered = useMemo(
    () => filterProxyNodes(nodes, query),
    [nodes, query]
  );

  const saveResult = (nodeId, value) => {
    setResults((current) => ({
      ...current,
      [nodeId]: normalizeProxyNodeTestResult(value),
    }));
  };

  const testOne = async (node) => {
    if (!subscription || testingAll || testingIds.includes(node.id)) return;
    setTestingIds((current) => [...current, node.id]);
    try {
      const response = await API.post(
        `/api/proxy-subscription/${subscription.id}/nodes/${node.id}/test`
      );
      if (!response.data?.success) {
        saveResult(node.id, {
          status: 'failed',
          message: response.data?.message,
        });
      } else {
        saveResult(node.id, response.data.data);
      }
    } catch (error) {
      saveResult(node.id, { status: 'failed', message: error?.message });
      showError(error);
    } finally {
      setTestingIds((current) => current.filter((id) => id !== node.id));
    }
  };

  const testAll = async () => {
    if (!subscription || testingAll || testingIds.length) return;
    setTestingAll(true);
    try {
      const response = await API.post(
        `/api/proxy-subscription/${subscription.id}/nodes/test`
      );
      if (!response.data?.success) throw new Error(response.data?.message);
      const payload = response.data.data;
      const entries = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.results)
        ? payload.results
        : [];
      setResults((current) => {
        const next = { ...current };
        entries.forEach((entry) => {
          const nodeId = entry.node_id ?? entry.id;
          if (nodeId !== undefined)
            next[nodeId] = normalizeProxyNodeTestResult(entry);
        });
        return next;
      });
    } catch (error) {
      showError(error);
    } finally {
      setTestingAll(false);
    }
  };

  const renderResult = (node) => {
    const result = results[node.id];
    if (!result)
      return (
        <span className='proxy-test-pending'>
          {t('setting.proxy_subscription.test.not_tested')}
        </span>
      );
    return (
      <div className={`proxy-test-result is-${result.status}`}>
        <Label
          basic
          size='tiny'
          color={
            result.status === 'success'
              ? 'green'
              : result.status === 'unsupported'
              ? 'orange'
              : result.status === 'failed'
              ? 'red'
              : 'grey'
          }
        >
          {t(`setting.proxy_subscription.test.status_${result.status}`)}
        </Label>
        <span>
          <b>{t('setting.proxy_subscription.test.mode')}</b>
          <code>
            {t(`setting.proxy_subscription.test.mode_${result.mode}`)}
          </code>
        </span>
        {result.latency !== null && (
          <span>
            <b>{t('setting.proxy_subscription.test.latency')}</b>
            <code>{result.latency} ms</code>
          </span>
        )}
        {result.lastTestTime && (
          <span>
            <b>{t('setting.proxy_subscription.test.last_test')}</b>
            <code>{formatNodeTimestamp(result.lastTestTime)}</code>
          </span>
        )}
        {result.status === 'success' ? (
          <small>{t('setting.proxy_subscription.test.success')}</small>
        ) : (
          result.message && <small>{result.message}</small>
        )}
      </div>
    );
  };

  const stateKey = loading
    ? 'loading'
    : nodes.length && query.trim()
    ? 'no_search'
    : 'empty';

  return (
    <Modal
      open={open}
      onClose={testingAll || testingIds.length ? undefined : onClose}
      size='large'
      closeIcon={!testingAll && !testingIds.length}
      className='studio-modal proxy-nodes-modal'
      closeOnDimmerClick={!testingAll && !testingIds.length}
    >
      <Modal.Header>
        <span className='proxy-modal-icon' aria-hidden='true'>
          <Icon name='sitemap' />
        </span>
        <div>
          <span className='studio-section-eyebrow'>PROXY NODES</span>
          {t('setting.proxy_subscription.nodes_title')}
          <small>{subscription?.name || ''}</small>
        </div>
      </Modal.Header>
      <Modal.Content scrolling>
        <div className='proxy-nodes-toolbar'>
          <Form.Input
            fluid
            icon='search'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('setting.proxy_subscription.nodes_search')}
            aria-label={t('setting.proxy_subscription.nodes_search_label')}
          />
          <Button
            primary
            onClick={testAll}
            loading={testingAll}
            disabled={
              loading || testingAll || !!testingIds.length || !nodes.length
            }
            aria-label={t('setting.proxy_subscription.test.all')}
          >
            <Icon name='heartbeat' />
            {t('setting.proxy_subscription.test.all')}
          </Button>
        </div>
        <div className='proxy-security-note'>
          <Icon name='shield alternate' aria-hidden='true' />
          <span>{t('setting.proxy_subscription.nodes_hint')}</span>
        </div>
        {subscription && (
          <div className='proxy-nodes-summary'>
            {[
              ['request_count', formatProxyNodeMetric(subscription.request_count)],
              ['input_tokens', formatProxyNodeMetric(subscription.input_tokens)],
              ['output_tokens', formatProxyNodeMetric(subscription.output_tokens)],
              ['total_tokens', formatProxyNodeMetric(subscription.total_tokens)],
              [
                'avg_response_time',
                formatProxyNodeResponseTime(subscription.avg_response_time),
              ],
              [
                'last_response_time',
                formatProxyNodeResponseTime(subscription.last_response_time),
              ],
            ].map(([key, value]) => (
              <div key={key}>
                <strong>{value}</strong>
                <span>{t(`setting.proxy_subscription.metrics.${key}`)}</span>
              </div>
            ))}
          </div>
        )}
        {filtered.length ? (
          <div className='proxy-nodes-table-scroll'>
            <Table className='proxy-nodes-table' basic='very' compact>
              <Table.Header>
                <Table.Row>
                  {[
                    'name',
                    'protocol',
                    'address',
                    'state',
                    'usage',
                    'last_seen',
                    'test',
                  ].map((key) => (
                    <Table.HeaderCell
                      key={key}
                      style={globalStyles.tableHeader}
                    >
                      {t(`setting.proxy_subscription.node_table.${key}`)}
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filtered.map((node) => (
                  <Table.Row key={node.id} style={globalStyles.tableRow}>
                    <Table.Cell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span
                          aria-hidden='true'
                          style={{
                            display: 'inline-flex',
                            width: 26,
                            height: 26,
                            flex: '0 0 26px',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 8,
                            background: 'rgba(79, 99, 168, 0.09)',
                            color: '#4f63a8',
                            fontSize: 11,
                            fontWeight: 700,
                          }}
                        >
                          {(node.protocol || 'px').slice(0, 2).toUpperCase()}
                        </span>
                        <strong>{node.name || '-'}</strong>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <Label basic size='tiny'>
                        {node.protocol || '-'}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <code>{`${node.host || '-'}:${node.port || 0}`}</code>
                    </Table.Cell>
                    <Table.Cell>
                      <Label
                        basic
                        size='tiny'
                        color={node.active ? 'green' : 'grey'}
                      >
                        <Icon
                          name={node.active ? 'check circle' : 'minus circle'}
                          style={{ margin: '0 4px 0 0' }}
                        />
                        {node.active
                          ? t('setting.proxy_subscription.node_active')
                          : t('setting.proxy_subscription.node_inactive')}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <span style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                        {formatProxyNodeMetric(node.request_count)}
                        <span
                          style={{ color: '#94a3b8', margin: '0 6px 0 2px' }}
                        >
                          /
                        </span>
                        {formatProxyNodeMetric(node.total_tokens)}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      {formatNodeTimestamp(node.last_seen_time)}
                    </Table.Cell>
                    <Table.Cell className='proxy-node-test-cell'>
                      {renderResult(node)}
                      <Button
                        size='mini'
                        icon='heartbeat'
                        title={t('setting.proxy_subscription.test.one')}
                        aria-label={t(
                          'setting.proxy_subscription.test.one_label',
                          { name: node.name || node.id }
                        )}
                        loading={testingIds.includes(node.id)}
                        disabled={testingAll || testingIds.includes(node.id)}
                        onClick={() => testOne(node)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        ) : (
          <div className={`proxy-empty-state is-${stateKey}`}>
            <Icon
              name={
                loading ? 'circle notch' : query.trim() ? 'search' : 'sitemap'
              }
              loading={loading}
              aria-hidden='true'
            />
            <strong>
              {t(`setting.proxy_subscription.node_states.${stateKey}.title`)}
            </strong>
            <span>
              {t(
                `setting.proxy_subscription.node_states.${stateKey}.description`
              )}
            </span>
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={testingAll || !!testingIds.length}>
          {t('setting.proxy_subscription.close')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const formatNodeTimestamp = (value) =>
  value
    ? new Date(
        typeof value === 'number' && value < 1e12 ? value * 1000 : value
      ).toLocaleString()
    : '-';

export default ProxyNodesModal;
